import { css } from '@emotion/react';
import omit from 'lodash/omit';
import React, { forwardRef, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { AngularContext } from 'react-app';
import { Button, OverlayTrigger } from 'react-bootstrap';
import t, { listen as reactTranslateListen } from 'react-translate';
import NvSwitch from 'shared/components/inputs/nv-switch';
import NvTooltip from 'shared/components/nv-tooltip';
import useClickOutside from 'shared/hooks/use-click-outside';
import { mergeRefs } from 'shared/react-utils';
import { cloneDeepSerializable, useAppDispatch } from 'redux/store';
import { translateJourney, translateLecturePage, translateOutline, unsetTranslationPreference, updateUserPlatformLanguage } from 'redux/actions/users';
import { getFullCourseTimelineContent, resetAllTimelineCachedCourseLookUp } from 'redux/actions/timeline';
import { getCourseAliases, getCurrentCourse, isFontColorLightForCurrentCourse } from 'redux/selectors/course';
import { getCurrentUser } from 'redux/selectors/users';
import { gray5, gray2, white, primary, gray4, black, gray1 } from 'styles/global_defaults/colors';
import { popoverZIndex, standardSpacing, quarterSpacing, halfSpacing } from 'styles/global_defaults/scaffolding';
import LanguagePicker, { AUTOMATED_TRANSLATION_LANGUAGES, LanguageValue } from 'automated_translation/components/language-picker';
import useLanguageTranslation, { useLanguageEnglishTranslation } from 'automated_translation/hooks/use-language-translation';
import ClickableContainer from 'components/clickable-container';
import useAngularExpression from 'shared/hooks/use-angular-expression';
import useScrollTop from 'shared/hooks/use-scroll';
import NvGradientBorderButton from 'shared/components/button/nv-gradient-border-button';
import GradientAiIcon from 'lecture_pages/components/left-panel/novo-ai/components/shared/gradient-ai-icon';
import NvIcon from 'shared/components/nv-icon';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { loadCourse } from 'redux/actions/courses';

import { useCourseHomeUtils } from 'shared/hooks/use-course-home-utils';
import { config } from '../../../config/pendo.config.json';
import { config as platformConfig } from '../../../config/config.json';

const TRANSLATION_RELOAD_FLAG = 'showTranslationChangeOnReload';

const AutomatedTranslationMenu = () => {
  const dispatch = useAppDispatch();
  const getLanguageTranslation = useLanguageTranslation();
  const getLanguageEnglishTranslation = useLanguageEnglishTranslation();

  const currentUser = useSelector(getCurrentUser);
  const { currentCatalogId, lecturePage: { currentLectureId, isTranslating } } = useSelector((state) => state.app);
  const isCourseFontColorLight = useSelector(isFontColorLightForCurrentCourse);
  const currentCourse = useSelector(getCurrentCourse);
  const isTopHeaderScrolling = useScrollTop(document.getElementById('main-panel'));
  const aliases = useSelector((state) => getCourseAliases(state));

  const { injectServices } = useContext(AngularContext);
  const [$state, CurrentUserManager, CurrentCourseManager, CourseHomeManager] = injectServices(['$state', 'CurrentUserManager', 'CurrentCourseManager', 'CourseHomeManager']);
  const currentStateName = useAngularExpression(() => $state.current.name);
  const isJourneyHome = currentStateName === 'learning-journey-home';
  const isCourseHome = currentStateName === 'course-home';
  const isLecturePage = currentStateName === 'lecture-page';

  const hasTranslationSet = !!currentUser.translationPreferenceLanguage;
  const [autoTranslationIsOn, setAutoTranslationIsOn] = useState(hasTranslationSet);
  const [showOverlay, setShowOverlay] = useState(false);
  const [successMessageLanguage, setSuccessMessageLanguage] = useState(false);
  const [translationInProgress, setTranslationInProgress] = useState(false);
  const [isTranslatingPlatform, setIsTranslatingPlatform] = useState(false);
  const buttonRef = useRef<HTMLDivElement>();

  const platformPreferenceLanguage = AUTOMATED_TRANSLATION_LANGUAGES.find((language) => language.translationKey === CurrentUserManager.user.platformLanguage.toUpperCase());
  const platformPreferenceLanguageLabel = platformPreferenceLanguage && getLanguageTranslation(platformPreferenceLanguage?.translationKey);
  const originalLanguage = AUTOMATED_TRANSLATION_LANGUAGES.find((language) => language.translationKey === currentCourse.contentLanguage.toUpperCase());
  const originalLanguageLabel = getLanguageTranslation(originalLanguage.translationKey);

  const { isEditOrReorderMode } = useCourseHomeUtils();

  useEffect(() => {
    setAutoTranslationIsOn(!!currentUser.translationPreferenceLanguage && currentUser.translationPreferenceLanguage !== originalLanguage.isoCode);
  }, [currentUser.translationPreferenceLanguage]);

  useEffect(() => {
    if (isCourseHome || isLecturePage) {
      if (isTranslatingPlatform
        && !translationInProgress) {
        setTranslationInProgress(true);
      } else if (!isTranslatingPlatform
        && translationInProgress
        && successMessageLanguage !== false) {
        setTranslationInProgress(false);
        setTimeout(() => {
          showSuccessMessage(successMessageLanguage);
          setSuccessMessageLanguage(false);
        });
      }
    }
  }, [isTranslatingPlatform, isTranslating, translationInProgress, successMessageLanguage]);

  useEffect(() => (
    reactTranslateListen(
      () => {
        setIsTranslatingPlatform(false);
      },
    )
  ), []);

  useEffect(() => {
    const lang = successMessageLanguage?.toString();
    if (successMessageLanguage === null || lang === 'vi') {
      setIsTranslatingPlatform(false);
    }
  }, [successMessageLanguage]);

  const lastTranslation = currentUser.lastTranslationPreferenceLanguage ?? currentUser.translationPreferenceLanguage;
  const lastTranslationKey = AUTOMATED_TRANSLATION_LANGUAGES.find((language) => language.isoCode === lastTranslation)?.translationKey;

  const initialLanguageValue = lastTranslation && lastTranslationKey ? {
    isoCode: lastTranslation,
    label: `${getLanguageTranslation(lastTranslationKey)} (${getLanguageEnglishTranslation(lastTranslationKey)})`.replace(' ()', ''),
  } : null;

  const [language, setLanguage] = useState<LanguageValue>(initialLanguageValue);

  const applyTranslation = (turnOn, newLanguage) => {
    setAutoTranslationIsOn(turnOn);
    if (!turnOn && lastTranslation && lastTranslationKey) {
      setLanguage(
        {
          isoCode: lastTranslation,
          label: `${getLanguageTranslation(lastTranslationKey)} (${getLanguageEnglishTranslation(lastTranslationKey)})`.replace(' ()', ''),
        },
      );
    }
    const newLanguageCompleteInfo = newLanguage?.isoCode && AUTOMATED_TRANSLATION_LANGUAGES.find((lang) => lang.isoCode === newLanguage.isoCode);

    const selectedPlatformLanguage = platformConfig.platformLanguages.find(lang => lang.toUpperCase() === newLanguageCompleteInfo?.translationKey);

    if (isCourseHome) {
      setIsTranslatingPlatform(true);
      dispatch(
        turnOn
          ? translateOutline({
            language: newLanguage.isoCode,
            catalogId: currentCatalogId,
          })
          : unsetTranslationPreference(),
      ).then(() => (
        updatePlatformLanguage(selectedPlatformLanguage)
      )).then(() => (
        dispatch(getFullCourseTimelineContent({
          catalogId: currentCatalogId,
        }))
      )).then(() => (
        // We need to fetch the course data to get the latest aliases
        CurrentCourseManager.requestCourse(currentCatalogId, true)
      // eslint-disable-next-line newline-per-chained-call
      )).then((updatedCourse) => {
        CourseHomeManager.getCourseHome(currentCatalogId);
        CurrentUserManager.user.updateFromReact({
          translationPreferenceLanguage: newLanguage.isoCode,
          lastTranslationPreferenceLanguage: turnOn ? newLanguage.isoCode : lastTranslation,
        });
        // After getting the last course version, we update redux store
        dispatch(loadCourse(cloneDeepSerializable(updatedCourse)));
        setSuccessMessageLanguage(turnOn ? newLanguage.isoCode : null);
      });
    } else if (isLecturePage) {
      setIsTranslatingPlatform(true);
      dispatch(
        turnOn
          ? translateLecturePage({
            language: newLanguage.isoCode,
            catalogId: currentCatalogId,
            lecturePageId: currentLectureId,
          })
          : unsetTranslationPreference(),
      ).then(() => (
        updatePlatformLanguage(selectedPlatformLanguage)
      )).then(() => {
        // We need to fetch the course data to get the default Aliases
        if (!turnOn) {
          CurrentCourseManager.requestCourse(currentCatalogId, true).then((updatedCourse) => {
            dispatch(loadCourse(cloneDeepSerializable(updatedCourse)));
          });
        }
        const updatedObject = turnOn
          ? {
            translationPreferenceLanguage: newLanguage.isoCode,
            lastTranslationPreferenceLanguage: newLanguage.isoCode,
          }
          : {
            translationPreferenceLanguage: null,
            lastTranslationPreferenceLanguage: lastTranslation,
          };
        CurrentUserManager.user.updateFromReact(updatedObject);
        setSuccessMessageLanguage(turnOn ? newLanguage.isoCode : null);
      });
    } else if (isJourneyHome) {
      if (turnOn) {
        dispatch(translateJourney({
          language: newLanguage.isoCode,
          catalogId: currentCatalogId,
        })).then(() => {
          dispatch(resetAllTimelineCachedCourseLookUp());
          CurrentUserManager.user.updateFromReact({
            translationPreferenceLanguage: newLanguage.isoCode,
            lastTranslationPreferenceLanguage: newLanguage.isoCode,
          });
        });
      } else {
        dispatch(unsetTranslationPreference()).then(() => {
          CurrentUserManager.user.updateFromReact({
            translationPreferenceLanguage: null,
          });
        });
      }
    }
  };

  const updatePlatformLanguage = (selectedLanguage) => {
    if (selectedLanguage) {
      return CurrentUserManager.updatePlatformLanguage(
        selectedLanguage,
        TRANSLATION_RELOAD_FLAG,
      )
        .then(() => {
          CurrentUserManager.updateOrigamiLanguage(selectedLanguage);
        })
        .then(() => {
          CurrentUserManager.user.platformLanguage = selectedLanguage;
          dispatch(updateUserPlatformLanguage(selectedLanguage));
        });
    }
    return Promise.resolve();
  };

  const showSuccessMessage = useCallback((newLanguageIsoCode = null) => {
    const newLanguage = AUTOMATED_TRANSLATION_LANGUAGES.find(lang => lang.isoCode === newLanguageIsoCode);
    let message = '';
    if (newLanguage) {
      const languageLabel = getLanguageTranslation(newLanguage.translationKey);
      message = t.AUTOMATED_TRANSLATION.ALERT_MESSAGES.SUCCESS_CHANGING_LANGUAGE({ ...aliases.courseAliases, language: languageLabel });
    } else {
      message = t.AUTOMATED_TRANSLATION.ALERT_MESSAGES.SUCCESS_RESET_TO_ORIGINAL({ ...aliases.courseAliases, language: originalLanguageLabel });
    }
    dispatch(addAlertMessage({
      type: AlertMessageType.SUCCESS,
      header: t.FORM.SUCCESS_BANG(),
      message,
    }));
  }, [aliases.courseAliases, dispatch, getLanguageTranslation, originalLanguageLabel]);

  useEffect(() => {
    if (sessionStorage.getItem(TRANSLATION_RELOAD_FLAG) === 'true') {
      showSuccessMessage(currentUser.translationPreferenceLanguage);
      sessionStorage.removeItem(TRANSLATION_RELOAD_FLAG);
    }
  }, [currentUser.translationPreferenceLanguage, showSuccessMessage]);

  const selectLanguage = (lang) => {
    setLanguage(lang);

    if (lang
      && autoTranslationIsOn
      && currentUser.translationPreferenceLanguage !== lang.isoCode
      && !isCourseHome
      && !isLecturePage
    ) {
      applyTranslation(true, lang);
    }
  };

  // These are the default styles.
  // They should be used for lesson page, when autoTranslation is NOT on
  // Or, when the course font color is black (or we're scrolling) and autoTranslation is NOT on
  let iconColor = black;
  let backgroundColor;
  let borderColor = black;

  if (isCourseHome && !isTopHeaderScrolling && isCourseFontColorLight) {
    if (autoTranslationIsOn) {
      iconColor = primary;
      backgroundColor = white;
      borderColor = white;
    } else {
      iconColor = white;
      borderColor = white;
    }
  }
  if (isCourseHome && (isTopHeaderScrolling || !isCourseFontColorLight)) {
    if (autoTranslationIsOn) {
      iconColor = white;
      backgroundColor = gray1;
      borderColor = gray1;
    }
  }
  if (isLecturePage && autoTranslationIsOn) {
    iconColor = white;
    backgroundColor = primary;
    borderColor = primary;
  }
  if (isJourneyHome && isCourseFontColorLight) {
    if (autoTranslationIsOn) {
      iconColor = primary;
      backgroundColor = white;
      borderColor = white;
    } else {
      iconColor = white;
      borderColor = white;
    }
  }
  if (isJourneyHome && autoTranslationIsOn && !isCourseFontColorLight) {
    iconColor = white;
    backgroundColor = black;
    borderColor = black;
  }
  if (!isLecturePage && !isCourseHome && !isJourneyHome) {
    iconColor = gray4;
    borderColor = gray4;
  }

  const iconStyles = css`
    .icon-wrapper {
      color: ${iconColor};
      padding: ${halfSpacing}px;
      padding-top: 13px;
      padding-bottom: 7px;
    }

    .icon-translate {
      padding: 3px;
      border: 2px solid ${borderColor};
      border-radius: 4px;
      font-size: 14px;

      ${backgroundColor && css`
        background-color: ${backgroundColor};
      `}
    }
  `;


  if (!isLecturePage && !isCourseHome && !isJourneyHome) {
    return (
      <ClickableContainer
        disabled
        css={iconStyles}
        data-qa={config.pendo.automatedTranslation.automatedTranslationButton}
        aria-label={t.AUTOMATED_TRANSLATION.AUTO_TRANSLATE()}
        isFocusable={!isEditOrReorderMode}
      >
        <div className='pb-1'>
          <NvTooltip
            placement='bottom'
            text={t.AUTOMATED_TRANSLATION.AUTO_TRANSLATION_NOT_AVAILABLE()}
          >
            <div className='icon-wrapper pl-2'>
              <i className='icon icon-small icon-translate' />
            </div>
          </NvTooltip>
        </div>
      </ClickableContainer>
    );
  }

  const revertLanguage = () => {
    const newLanguage = {
      ...originalLanguage,
      label: `${getLanguageTranslation(originalLanguage.translationKey)} (${getLanguageEnglishTranslation(originalLanguage.translationKey)})`.replace(' ()', ''),
    };
    applyTranslation(false, newLanguage);
    setShowOverlay(false);
  };

  const LanguageSwitch = () => (
    <div className='d-flex justify-content-between mb-4'>
      <div>{t.AUTOMATED_TRANSLATION.TURN_ON()}</div>
      {language ? (
        <NvSwitch
          enabledIcon='gen-ai'
          checked={autoTranslationIsOn}
          onChange={(change) => applyTranslation(change, language)}
          disabled={isTranslating}
          data-qa={config.pendo.automatedTranslation.applyTranslationSwitch}
        />
      ) : (
        <NvTooltip text={t.AUTOMATED_TRANSLATION.PLEASE_SELECT_A_LANGUAGE_FIRST()}>
          <NvSwitch
            enabledIcon='gen-ai'
            checked={autoTranslationIsOn}
            onChange={(change) => applyTranslation(change, language)}
            disabled
            data-qa={config.pendo.automatedTranslation.applyTranslationSwitch}
          />
        </NvTooltip>
      )}
    </div>
  );

  const LanguageSelector = () => {
    const label = isJourneyHome
      ? t.AUTOMATED_TRANSLATION.SELECT_A_LANGUAGE_FOR_AUTO_TRANSLATION()
      : t.AUTOMATED_TRANSLATION.VIEW_CONTENT_IN();
    const languageSelectorStyle = css`
    .language-change-picker {
      ${!isJourneyHome && css`
        padding: ${quarterSpacing}px 0 ${halfSpacing}px;
      `}
    }
    `;
    return (
      <div css={languageSelectorStyle}>
        <div className='label mb-1'>{label}</div>
        <LanguagePicker
          value={language}
          className='language-change-picker'
          onChange={(lang) => selectLanguage(lang)}
          dataQa={config.pendo.automatedTranslation.languagesPicker}
        />
      </div>
    );
  };


  const UpdateButton = () => {
    const isOriginalLanguage = lastTranslation === originalLanguage.isoCode;
    const isDisabled = (language?.isoCode === lastTranslation && lastTranslation === currentUser.translationPreferenceLanguage) || (!lastTranslation && !language);
    const updateButtonStyle = css`
      display: grid;
      justify-items: center;
      .ai-btn {
        width: fit-content;
        margin-top: ${halfSpacing}px;
        &.disabled {
          .icon {
            color: ${gray5};
          }
          .text-regular {
            color: ${gray4}
          }
        }
      }
      .description-text {
        padding-bottom: ${halfSpacing}px;
      }
      .bs4-btn-link {
        padding: ${halfSpacing}px 0 0;
      }
      .translation-warning {
        padding-top: ${halfSpacing}px;
      }
    `;
    const handleClick = () => {
      const isSelectingOriginalLanguage = language.isoCode === originalLanguage.isoCode;
      applyTranslation(!isSelectingOriginalLanguage, language);
      setShowOverlay(false);
    };
    return (
      <div css={updateButtonStyle}>
        {
          !isDisabled
          && (
            <div className='description-text gray-2'>
              {
                t.AUTOMATED_TRANSLATION.LANGUAGE_WARNING()
              }
            </div>
          )

        }
        <NvGradientBorderButton
          onClick={handleClick}
          className={`ai-btn justify-content-center align-items-center py-2 px-5${isDisabled ? ' disabled' : ''}`}
          dataQa={config.pendo.novoAiTranslation.updateLanguage}
          disabled={isDisabled}
        >
          {
            isDisabled
              ? <NvIcon icon='gen-ai' size='small' className='pr-2 disabled' />
              : <GradientAiIcon icon='gen-ai' size='small' className='pr-2' />
          }
          <span className='text-regular bold'>{t.AUTOMATED_TRANSLATION.UPDATE()}</span>
        </NvGradientBorderButton>
        {
          !isOriginalLanguage && autoTranslationIsOn
          && (
            <Button
              variant='link'
              onClick={revertLanguage}
              data-qa={config.pendo.novoAiTranslation.revertLanguage}
            >
              { t.AUTOMATED_TRANSLATION.REVERT_TO_ORIGINAL(originalLanguageLabel) }
            </Button>
          )
        }
        <div className='translation-warning gray-2'>
          {
            !isDisabled && t.AUTOMATED_TRANSLATION.TRANSLATION_WARNING()
          }
        </div>
      </div>
    );
  };

  return (
    <OverlayTrigger
      show={showOverlay}
      placement='bottom-end'
      transition={false}
      overlay={(props) => (
        <AccessibleOverlay
          onClose={(closedWithKeyboard) => {
            setShowOverlay(false);

            if (closedWithKeyboard) {
              buttonRef.current.focus();
            }
          }}
          {...omit(props, ['popper', 'arrowProps']) as any}
        >
          { isJourneyHome && <LanguageSwitch /> }
          <LanguageSelector />
          { !isJourneyHome && <UpdateButton /> }
        </AccessibleOverlay>
      )}
    >
      {({ ref, ...props }) => (
        <ClickableContainer
          css={iconStyles}
          ref={mergeRefs(ref, buttonRef)}
          onClick={() => setShowOverlay(true)}
          data-qa={config.pendo.automatedTranslation.automatedTranslationButton}
          aria-label={t.AUTOMATED_TRANSLATION.AUTO_TRANSLATE()}
          aria-haspopup
          aria-expanded={showOverlay}
          isFocusable={!isEditOrReorderMode}
          {...props}
        >
          <div className='pb-1'>
            <NvTooltip
              text={autoTranslationIsOn ? t.AUTOMATED_TRANSLATION.CLICK_TO_TURN_OFF_THE_TRANSLATION() : t.AUTOMATED_TRANSLATION.AUTO_TRANSLATE()}
              placement='bottom'
            >
              <div className='icon-wrapper'>
                <i className='icon icon-small icon-translate' />
              </div>
            </NvTooltip>
          </div>
        </ClickableContainer>
      )}
    </OverlayTrigger>
  );
};

type AccessibleOverlayProps = Omit<React.ComponentProps<'div'>, 'tabIndex' | 'className'> & {
  show: boolean,
  onClose: (closedWithKeyboard: boolean) => void,
};

const AccessibleOverlay = forwardRef<HTMLDivElement, AccessibleOverlayProps>((props, ref) => {
  const {
    show,
    onClose,
    ...restProps
  } = props;

  const innerRef = useRef<HTMLDivElement>();
  const onCloseRef = useRef<Function>();
  onCloseRef.current = onClose;

  const styles = css`
    width: 340px;
    border-radius: 4px;
    background-color: white;
    border: 1px solid ${gray5};
    z-index: ${popoverZIndex};
    padding: ${standardSpacing}px;

    .label {
      color: ${gray2};
    }
  `;


  useEffect(() => {
    if (innerRef.current) {
      innerRef.current.focus();
    }
  }, [show]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose(true);
    }
  };

  useClickOutside(innerRef, () => onClose(false));

  return (
    <div
      css={styles}
      tabIndex={-1}
      className='react-app'
      onKeyDown={handleKeyDown}
      ref={mergeRefs(ref, innerRef)}
      {...restProps}
    />
  );
});

export default AutomatedTranslationMenu;
